import { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle,
  Eye,
  Check,
  UserX,
  Monitor,
  MapPin,
  Bot,
  Users,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { fraudAPI } from '../lib/api';
import { formatDateTime, getSeverityColor } from '../lib/utils';
import { toast } from 'sonner';

const alertTypeIcons = {
  multiple_accounts: UserX,
  chip_dumping: Monitor,
  collusion: Users,
  vpn: MapPin,
  bot_activity: Bot,
};

const alertTypeLabels = {
  multiple_accounts: 'Duplicate account',
  chip_dumping: 'Chip dumping',
  collusion: 'Collusion',
  vpn: 'VPN',
  bot_activity: 'Bot activity',
  account_banned: 'Banned player',
  self_exclusion: 'Self-exclusion',
  withdrawal_risk: 'Withdrawal risk',
};

export default function FraudSecurity() {
  const [alerts, setAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [resolvedFilter, setResolvedFilter] = useState('false');
  const [severityFilter, setSeverityFilter] = useState('');
  const [activeTab, setActiveTab] = useState('alerts');
  const [resolveDialog, setResolveDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [summary, setSummary] = useState({});

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        resolved: resolvedFilter === 'all' ? undefined : resolvedFilter === 'true',
        severity: severityFilter || undefined,
      };
      const response = await fraudAPI.getAlerts(params);
      setAlerts(response.data.alerts);
      setTotalAlerts(response.data.total);
      setSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch fraud alerts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
      };
      const response = await fraudAPI.getAuditLogs(params);
      setAuditLogs(response.data.logs);
      setTotalLogs(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'alerts') {
      fetchAlerts();
    } else {
      fetchAuditLogs();
    }
  }, [page, resolvedFilter, severityFilter, activeTab]);

  const handleResolve = async () => {
    try {
      await fraudAPI.resolveAlert(selectedAlert.id, resolveNotes);
      toast.success('Alert resolved');
      setResolveDialog(false);
      setSelectedAlert(null);
      setResolveNotes('');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const openResolveDialog = (alert) => {
    setSelectedAlert(alert);
    setResolveDialog(true);
  };

  return (
    <div data-testid="fraud-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Fraud & Security Center</h1>
        <p className="page-subtitle">Monitor suspicious activities and security events</p>
      </div>

      {/* Stats */}
      <div className="widget-grid mb-6">
        <div className="stat-card pulse-alert">
          <AlertTriangle className="w-5 h-5 text-red-400 mb-2" />
          <p className="stat-value text-red-400">{summary.unresolved || 0}</p>
          <p className="stat-label">Active Alerts</p>
        </div>
        <div className="stat-card">
          <UserX className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{summary.account_banned || 0}</p>
          <p className="stat-label">Banned players</p>
        </div>
        <div className="stat-card">
          <Users className="w-5 h-5 text-violet-400 mb-2" />
          <p className="stat-value">{summary.withdrawal_risk || 0}</p>
          <p className="stat-label">Withdrawal risk</p>
        </div>
        <div className="stat-card">
          <Bot className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{summary.multiple_accounts || 0}</p>
          <p className="stat-label">Duplicate phones</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); }} className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="alerts" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Fraud Alerts
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Shield className="w-4 h-4 mr-2" />
            Audit Logs
          </TabsTrigger>
        </TabsList>

        {/* Fraud Alerts Tab */}
        <TabsContent value="alerts">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">Fraud Alerts</h3>
              <div className="filter-bar">
                <Select value={resolvedFilter} onValueChange={setResolvedFilter}>
                  <SelectTrigger className="w-36" data-testid="resolved-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="false">Unresolved</SelectItem>
                    <SelectItem value="true">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={severityFilter} onValueChange={setSeverityFilter}>
                  <SelectTrigger className="w-36" data-testid="severity-filter">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Alert</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Detected</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                      No alerts found
                    </TableCell>
                  </TableRow>
                ) : (
                  alerts.map((alert) => {
                    const Icon = alertTypeIcons[alert.alert_type] || AlertTriangle;
                    return (
                      <TableRow key={alert.id} className="table-row" data-testid={`alert-row-${alert.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getSeverityColor(alert.severity)}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className="font-medium">{alertTypeLabels[alert.alert_type] || alert.alert_type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{alert.user_name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{alert.user_phone || alert.user_id || '—'}</p>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm">{alert.description}</TableCell>
                        <TableCell>
                          <span className={`badge border ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{formatDateTime(alert.created_at)}</TableCell>
                        <TableCell>
                          <span className={`badge ${alert.is_resolved ? 'badge-success' : 'badge-danger'}`}>
                            {alert.is_resolved ? 'Resolved' : 'Active'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {!alert.is_resolved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openResolveDialog(alert)}
                              data-testid={`resolve-alert-${alert.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Resolve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, totalAlerts)} of {totalAlerts}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= totalAlerts}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">Admin Activity Logs</h3>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Admin</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id} className="table-row">
                      <TableCell>
                        <p className="font-medium">{log.admin_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{log.admin_id.slice(0, 12)}...</p>
                      </TableCell>
                      <TableCell>
                        <span className="badge badge-info">{log.action}</span>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{log.entity_type}</p>
                        <p className="text-xs text-muted-foreground font-mono">{log.entity_id.slice(0, 12)}...</p>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {JSON.stringify(log.details).slice(0, 50)}...
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(log.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, totalLogs)} of {totalLogs}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= totalLogs}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog} onOpenChange={setResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Alert</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Add resolution notes for this fraud alert.
            </p>
            <Textarea
              placeholder="Enter resolution notes..."
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              rows={4}
              data-testid="resolve-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(false)}>Cancel</Button>
            <Button onClick={handleResolve} data-testid="confirm-resolve-btn">
              <Check className="w-4 h-4 mr-2" />
              Mark as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
